// appInsights.ts
import {
  ApplicationInsights,
  IConfig,
  IConfiguration,
} from "@microsoft/applicationinsights-web";

export enum ENV {
  DEVELOPMENT = "development",
  QA = "qa",
  UAT = "uat",
  PRODUCTION = "production",
}

export enum LOG_LEVEL {
  DEBUG = "debug",
  INFO = "info",
  WARN = "warn",
  ERROR = "error",
}

// Default log levels per environment
const DEFAULT_LOG_LEVELS: Record<ENV, LOG_LEVEL> = {
  [ENV.DEVELOPMENT]: LOG_LEVEL.DEBUG,
  [ENV.QA]: LOG_LEVEL.INFO,
  [ENV.UAT]: LOG_LEVEL.WARN,
  [ENV.PRODUCTION]: LOG_LEVEL.ERROR,
};

class AppInsightsService {
  private static instance: AppInsightsService;
  private appInsights: ApplicationInsights | null = null;
  private isInitialized = false;
  private readonly env = import.meta.env.MODE as ENV;

  public readonly logLevel: LOG_LEVEL;

  private constructor() {
    // Pick log level from env or fallback to default
    this.logLevel =
      (import.meta.env.VITE_LOG_LEVEL as LOG_LEVEL) ||
      DEFAULT_LOG_LEVELS[this.env as ENV] ||
      LOG_LEVEL.ERROR;

    this.initialize();
  }

  public static getInstance(): AppInsightsService {
    if (!AppInsightsService.instance) {
      AppInsightsService.instance = new AppInsightsService();
    }
    return AppInsightsService.instance;
  }

  private initialize(): void {
    try {
      let connectionString: string | undefined;
      let instrumentationKey: string | undefined;

      switch (this.env) {
        case ENV.DEVELOPMENT:
          connectionString = import.meta.env
            .VITE_APPINSIGHTS_CONNECTION_STRING_DEV;
          instrumentationKey = import.meta.env.VITE_APPINSIGHTS_KEY_DEV;
          break;
        case ENV.QA:
          connectionString = import.meta.env
            .VITE_APPINSIGHTS_CONNECTION_STRING_QA;
          instrumentationKey = import.meta.env.VITE_APPINSIGHTS_KEY_QA;
          break;
        case ENV.UAT:
          connectionString = import.meta.env
            .VITE_APPINSIGHTS_CONNECTION_STRING_UAT;
          instrumentationKey = import.meta.env.VITE_APPINSIGHTS_KEY_UAT;
          break;
        case ENV.PRODUCTION:
          connectionString = import.meta.env
            .VITE_APPINSIGHTS_CONNECTION_STRING_PROD;
          instrumentationKey = import.meta.env.VITE_APPINSIGHTS_KEY_PROD;
          break;
      }

      if (!instrumentationKey && !connectionString) {
        if (this.env === ENV.DEVELOPMENT) {
          console.info(
            "AppInsights",
            "Application Insights configuration not found. Skipping initialization."
          );
        }
        return;
      }

      const config: IConfig & IConfiguration = {
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        disableExceptionTracking: false,
        disableAjaxTracking: false,
        samplingPercentage: 100,
        sessionRenewalMs: 30 * 60 * 1000,
        sessionExpirationMs: 24 * 60 * 60 * 1000,
      };

      if (connectionString) {
        config.connectionString = connectionString;
      } else if (instrumentationKey) {
        config.instrumentationKey = instrumentationKey;
      }

      this.appInsights = new ApplicationInsights({ config });
      this.appInsights.loadAppInsights();

      this.appInsights.addTelemetryInitializer((envelope) => {
        envelope.tags = envelope.tags || {};
        envelope.tags["ai.cloud.role"] = "frontend";

        envelope.data = envelope.data || {};
        envelope.data.baseData = envelope.data.baseData || {};
        envelope.data.baseData.properties = {
          environment: this.env,
          version: import.meta.env.VITE_APP_VERSION || "unknown",
          userAgent: navigator.userAgent,
        };

        return true;
      });

      this.isInitialized = true;

      if (this.env === ENV.DEVELOPMENT) {
        console.info(
          "AppInsights",
          "Application Insights initialized successfully (Dev)."
        );
      }
    } catch (error) {
      this.appInsights = null;
      this.isInitialized = false;

      if (this.env === ENV.DEVELOPMENT) {
        console.error(
          "AppInsights",
          "Failed to initialize Application Insights",
          error
        );
      }
    }
  }

  public isReady(): boolean {
    return this.isInitialized && this.appInsights !== null;
  }

  public trackTrace(
    message: string,
    severityLevel?: number,
    properties?: Record<string, string>
  ): void {
    this.appInsights?.trackTrace({ message, severityLevel, properties });
  }

  public trackException(
    exception: Error,
    properties?: Record<string, string>,
    severityLevel?: number
  ): void {
    this.appInsights?.trackException({ exception, properties, severityLevel });
  }
}

export const appInsightsService = AppInsightsService.getInstance();
export default appInsightsService;
